import Divider from '@mui/material/Divider';
import * as Sentry from '@sentry/react';
import { FormApi } from 'final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ConnectedProps, connect } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';

import { notify } from '@/actions/ui';
import {
  SignupType,
  SocialSignup,
  formatAuthPath,
  isValidPassword,
  loginWithSocial,
  signup,
} from '@/auth';
import AuthLayout from '@/auth/components/AuthLayout';
import { PasswordText } from '@/auth/components/PasswordTextField';
import { SocialConnectStep } from '@/auth/components/SocialConnectStep/SocialConnectStep';
import SocialSignInButtons, {
  isSocialSignInEnabled,
} from '@/auth/components/SocialSignInButtons/SocialSignInButtons';
import { pendingSocialLinkState } from '@/auth/utils/pendingSocialLinkHandoff';
import { SignInHandoff } from '@/auth/utils/signInHandoff';
import { SocialProvider, socialProviderName, startSocialSignIn } from '@/auth/utils/socialSignIn';
import {
  SOCIAL_SIGNUP_ENDED,
  socialErrorMessage,
  socialSignInFailure,
} from '@/auth/utils/socialSignInFailure';
import {
  expiredSocialSignupProvider,
  forgetSocialSignup,
  readSocialSignupHandoff,
} from '@/auth/utils/socialSignupHandoff';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import { Checkbox, PhoneInput, TextField } from '@/components/FormAdapters';
import Link from '@/components/Link';
import TextButton from '@/componentsv2/Button';
import { policies } from '@/core/agreements';
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { Tracking, getTracking } from '@/core/tracking';
import { GroupDomain } from '@/group';
import { useApp } from '@/hooks/useAppContext';
import store, { RootState } from '@/store';
import { isEmailValid, isPhoneValid, normalizeSpace } from '@/utils';
import { hasOnlyLetters } from '@/utils/reducer';

import JoinNetwork, { useStyles } from './JoinNetwork';
import { handleSignupError } from './handleSignupError';

interface SignupProps {
  domain?: GroupDomain;
  tags?: string[];
  signupType: SignupType;
  nextUrl?: string;
  signinLink?: string;
  signinNextUrl?: string;
  invite?: string;
  defaultEmail?: string;
  title?: string;
  shortAboutText?: string;
  longAboutText?: string;
  children?: React.ReactNode;
}

const connector = connect((state: RootState) => ({
  viewer: state.viewer,
}));

const EXPERT_PART_POLICY = policies['expert-participation-agreement'];
const TOU_POLICY = policies['terms-of-use'];
const PRIVACY_POLICY = policies.privacy;
const SMS_OPT_IN_POLICY = policies['sms-opt-in-agreement'];

// The slice of react-final-form's form API the social flow writes through.
type FormFields = Pick<FormApi, 'batch' | 'change' | 'getState'>;

function prefill(
  form: FormFields,
  values: { first_name?: string; last_name?: string; email?: string }
): void {
  form.batch(() => {
    form.change('first_name', values.first_name);
    form.change('last_name', values.last_name);
    form.change('email', values.email);
  });
}

// ENG-6165: a Google (or, ENG-6169, Microsoft) account signing up (prefilled,
// no password) or one that already belongs to an OnFrontiers account.
// ENG-6192: CONNECT when the api lets the page connect it to that account
// without the password (proof NONE or EMAIL_CODE). ENG-6309: otherwise Log in
// takes the email and, by reference, the connection to make after sign-in.
type SocialState = { provider: SocialProvider } & (
  | { status: 'SIGN_UP'; email: string; social: SocialSignup }
  | {
      status: 'CONFIRM_ACCOUNT';
      email?: string;
      loginState?: { email: string; pendingSocialLink: string };
    }
  | {
      status: 'CONNECT';
      email: string;
      proof: 'NONE' | 'EMAIL_CODE';
      token: string;
      idToken: string;
    }
);

const Signup = ({
  viewer,
  domain,
  tags,
  signupType,
  nextUrl,
  signinLink,
  signinNextUrl,
  invite,
  defaultEmail,
  title,
  shortAboutText,
  longAboutText,
  children,
}: SignupProps & ConnectedProps<typeof connector>) => {
  const { graphql } = useApp();
  const navigate = useNavigate();

  const isExpertSignup = signupType === 'expert';

  const s = useStyles();
  // ENG-6311: the terms read at body-2, each box centred on its term's first
  // line. The 18px box has 4px padding (a 26px target) pulled up 4px and left
  // 4px, so its centre sits 9px down, on the first line's (17.76px / 2), and
  // its edge lines up with the fields.
  const termsCheckboxClasses = { root: 'p-4 -mt-4 [&_svg]:text-18' };
  const termsLabelClasses = { root: '-ml-4 items-start', label: 'body-2' };

  const { executeRecaptcha } = useGoogleReCaptcha();
  // A sign-up the login page started (ENG-6169) opens as that provider's.
  const { pathname, search, state: locationState } = useLocation();
  const [handoff] = useState(() => readSocialSignupHandoff(locationState));
  const [social, setSocial] = useState<SocialState | undefined>(
    handoff && {
      provider: handoff.social.provider,
      status: 'SIGN_UP',
      email: handoff.email,
      social: handoff.social,
    }
  );
  // A reload or another tab keeps the reference but not the sign-up: say so
  // over the plain form rather than leave it silently cold. Once per state,
  // which StrictMode's second effect run would otherwise repeat.
  const announcedExpiry = useRef<unknown>();
  useEffect(() => {
    if (announcedExpiry.current === locationState) return;
    announcedExpiry.current = locationState;
    const expired = expiredSocialSignupProvider(locationState);
    if (expired) {
      const message = socialErrorMessage(ERROR_CODES.AUTH_SOCIAL_SIGNUP_TOKEN_INVALID, expired);
      if (message) store.dispatch(notify(message, 'error'));
    }
  }, [locationState]);
  // One provider answer at a time; Join waits for it.
  const socialBusy = useRef(false);
  const [socialPending, setSocialPending] = useState(false);
  const socialSignup = social?.status === 'SIGN_UP' ? social.social : undefined;
  const socialEmail = social?.status === 'SIGN_UP' ? social.email : undefined;

  const signinPath = formatAuthPath({
    to: signinLink || '/login',
    domain,
    signupType,
    invite,
    next: signinNextUrl || nextUrl,
  });

  const initialValues = useMemo(
    () => ({
      email: handoff?.email ?? defaultEmail,
      first_name: handoff?.firstName,
      last_name: handoff?.lastName,
    }),
    [defaultEmail, handoff]
  );

  const expertAgreements = useMemo(() => {
    return isExpertSignup
      ? [
          {
            policy_code: EXPERT_PART_POLICY.apiKey,
            policy_label: EXPERT_PART_POLICY.name,
            policy_url: EXPERT_PART_POLICY.url,
          },
          ...((domain && domain.agreements) || []),
        ]
      : [];
  }, [isExpertSignup, domain]);

  const handleSubmit = useCallback(
    async (values: any, form: FormFields) => {
      const tracking = getTracking() || ({} as Tracking);
      try {
        const recaptchaToken = executeRecaptcha ? await executeRecaptcha('signup') : '';
        const now = new Date();
        await signup(
          graphql,
          {
            ...(socialSignup ? { social: socialSignup } : { password: values.password }),
            recaptcha_token: recaptchaToken,
            subdomain: domain && domain.subdomain,
            // ENG-6311: a social sign-up's form has no email field.
            email: socialEmail ?? values.email,
            phone: values.phone,
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            signup_type: signupType,
            landing_url: tracking.landing,
            invite,
            tags: tags ? (Array.isArray(tags) ? tags : [tags]) : [],
            //login: signupType === 'expert' || !domain?.group?.enforce_2fa,
            agreements: [
              {
                policy: TOU_POLICY.apiKey,
                accepted: !!values.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
              {
                policy: PRIVACY_POLICY.apiKey,
                accepted: !!values.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
              {
                policy: SMS_OPT_IN_POLICY.apiKey,
                accepted: !!values.accept_sms_opt_in_agreement,
                updated_at: now,
              },
              ...expertAgreements.map((a) => ({
                policy: a.policy_code,
                accepted: !!values[`accept_${a.policy_code}`],
                updated_at: now,
              })),
            ],
          },
          nextUrl || undefined
        );
      } catch (e: unknown) {
        const code = firstErrorCode(e);
        if (socialSignup && code && SOCIAL_SIGNUP_ENDED.has(code)) {
          store.dispatch(notify(socialErrorMessage(code, socialSignup.provider) ?? code, 'error'));
          setSocial(undefined);
          prefill(form, {});
          return undefined;
        }
        const errors = handleSignupError(e, (notice) => {
          const handoff: SignInHandoff = { email: socialEmail ?? values.email, notice };
          navigate(signinPath, { state: handoff });
        });
        // A social sign-up's form has no email field to show this on.
        if (socialSignup && errors?.email) {
          store.dispatch(notify(errors.email, 'error'));
          return undefined;
        }
        return errors;
      }
    },
    [
      executeRecaptcha,
      graphql,
      domain,
      signupType,
      invite,
      tags,
      expertAgreements,
      nextUrl,
      navigate,
      signinPath,
      socialSignup,
      socialEmail,
    ]
  );

  // "change" on the short form: not this person. Back to the plain form, at
  // its first field, since the line that held focus is gone.
  const firstNameInput = useRef<HTMLInputElement>(null);
  const handleChangeAccount = (form: FormFields) => {
    // Drop the reference from this history entry too, or Back here would
    // find it gone and say the sign-up expired.
    if (handoff) {
      forgetSocialSignup(locationState);
      navigate(`${pathname}${search}`, { replace: true, state: undefined });
    }
    setSocial(undefined);
    prefill(form, {});
    firstNameInput.current?.focus();
  };

  const showSocialFailure = (provider: SocialProvider, err: unknown) => {
    const { message, expected } = socialSignInFailure(err, provider);
    if (!expected) Sentry.captureException(err);
    store.dispatch(notify(message, 'error'));
  };

  const handleSocialCredential = async (
    provider: SocialProvider,
    idToken: string,
    form: FormFields
  ) => {
    // A Join in flight owns the form: a provider answer now would race it.
    if (socialBusy.current || form.getState().submitting) return;
    socialBusy.current = true;
    setSocialPending(true);
    let redirecting = false;
    try {
      const start = await startSocialSignIn(graphql, provider, idToken);
      if (start.status === 'SIGN_IN') {
        // As the "Sign in" link does: a domain signup returns to its network form.
        await loginWithSocial(graphql, {
          provider,
          username: start.username,
          idToken,
          next: signinNextUrl || nextUrl,
        });
        redirecting = true;
        return;
      }
      if (start.status === 'CONFIRM_ACCOUNT') {
        // An earlier social sign-up's prefill belongs to another account.
        if (social?.status === 'SIGN_UP') prefill(form, {});
        const { email, connect } = start;
        setSocial(
          email && connect?.token
            ? { provider, status: 'CONNECT', email, ...connect, idToken }
            : {
                provider,
                status: 'CONFIRM_ACCOUNT',
                email,
                loginState: email
                  ? { email, ...pendingSocialLinkState({ provider, idToken, email }) }
                  : undefined,
              }
        );
        return;
      }
      prefill(form, { first_name: start.firstName, last_name: start.lastName, email: start.email });
      setSocial({
        provider,
        status: 'SIGN_UP',
        email: start.email,
        social: { provider, signupToken: start.signupToken, idToken },
      });
    } catch (err) {
      showSocialFailure(provider, err);
    } finally {
      socialBusy.current = false;
      if (!redirecting) setSocialPending(false);
    }
  };

  const validate = useCallback(
    (values: any) => {
      const errors: { [key: string]: string } = {};

      if (!(values.first_name || '').trim()) {
        errors.first_name = 'Required';
      }

      if (values.first_name && !hasOnlyLetters(values.first_name)) {
        errors.first_name = 'Only letters allowed';
      }

      if (!(values.last_name || '').trim()) {
        errors.last_name = 'Required';
      }

      if (values.last_name && !hasOnlyLetters(values.last_name)) {
        errors.last_name = 'Only letters allowed';
      }

      // A social sign-up's email is the provider's, with no field to check.
      if (!socialSignup) {
        if (!values.email) {
          errors.email = 'Required';
        } else if (!isEmailValid(values.email)) {
          errors.email = 'Invalid email address';
        }
      }

      if (isExpertSignup && !values.phone) {
        errors.phone = 'Required';
      }

      if (values.phone && !isPhoneValid(values.phone)) {
        errors.phone = 'Phone must be valid';
      }

      if (!socialSignup && !isValidPassword(values.password)) {
        errors.password = 'Password does not meet the requirements';
      }

      if (!values.accept_privacy_and_terms_of_use) {
        errors.accept_privacy_and_terms_of_use = 'Required';
      }

      if (isExpertSignup && !values.accept_sms_opt_in_agreement) {
        errors.accept_sms_opt_in_agreement = 'Required';
      }

      expertAgreements.forEach((a) => {
        const fieldName = `accept_${a.policy_code}`;
        if (!values[fieldName]) {
          errors[fieldName] = 'Required';
        }
      });

      return errors;
    },
    [expertAgreements, isExpertSignup, socialSignup]
  );

  const showJoin = viewer.id && !!domain;

  const renderConnectStep = ({
    provider,
    email,
    proof,
    token,
    idToken,
  }: Extract<SocialState, { status: 'CONNECT' }>) => (
    <SocialConnectStep
      provider={provider}
      email={email}
      proof={proof}
      token={token}
      onConnected={(username) =>
        loginWithSocial(graphql, {
          provider,
          username,
          idToken,
          next: signinNextUrl || nextUrl,
          connected: true,
        })
      }
      onCancel={() => setSocial(undefined)}
    />
  );

  return (
    <AuthLayout
      headerChildren={children}
      domain={domain}
      title={title}
      shortAboutText={shortAboutText}
      longAboutText={longAboutText}
    >
      {showJoin && <JoinNetwork viewer={viewer} domain={domain} isExpertSignup={isExpertSignup} />}
      {!showJoin && social?.status === 'CONNECT' && renderConnectStep(social)}
      {!showJoin && (
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          subscription={{
            submitting: true,
            values: true,
            initialValues: true,
            touched: true,
          }}
        >
          {({ handleSubmit, submitting, form }: any) => {
            return (
              // Hidden, not unmounted, while connecting: Cancel brings back what was typed.
              <form hidden={social?.status === 'CONNECT'} onSubmit={(e: any) => handleSubmit(e)}>
                {social?.status === 'SIGN_UP' && (
                  <p className="m-[0] break-words text-center text-on-surface">
                    <span>{`Signing up as ${social.email} via ${socialProviderName(social.provider)}`}</span>
                    {/* No break before the dot: "· change" stays on the provider's line. */}
                    <span className="whitespace-nowrap">
                      <span aria-hidden="true">{'\u00a0· '}</span>
                      <TextButton
                        className="min-h-48"
                        variant="tertiary"
                        // The Join in flight carries this sign-up.
                        disabled={submitting}
                        onClick={() => handleChangeAccount(form)}
                      >
                        change
                      </TextButton>
                    </span>
                  </p>
                )}
                <Field
                  component={TextField}
                  name="first_name"
                  inputRef={firstNameInput}
                  label="First Name"
                  variant="outlined"
                  placeholder="Please provide your full first name"
                  required={!isExpertSignup}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  changeOnBlur={false}
                  inputProps={{ maxLength: 255 }}
                  format={normalizeSpace}
                />
                <Field
                  component={TextField}
                  name="last_name"
                  label="Last Name"
                  variant="outlined"
                  placeholder="Please provide your full last name"
                  required={!isExpertSignup}
                  changeOnBlur={false}
                  inputProps={{ maxLength: 255 }}
                  format={normalizeSpace}
                />
                <Field
                  component={PhoneInput}
                  name="phone"
                  label="Phone"
                  variant="outlined"
                  showExampleOnError
                />
                {!socialSignup && (
                  <>
                    <Field
                      component={TextField}
                      changeOnBlur={false}
                      name="email"
                      label="Email"
                      variant="outlined"
                      inputProps={{ autoComplete: 'off' }}
                      required={!isExpertSignup}
                    />
                    <Field
                      component={PasswordText}
                      name="password"
                      label="Password"
                      variant="outlined"
                      showHelp
                      inputProps={{ autoComplete: 'new-password' }}
                      required={!isExpertSignup}
                    />
                  </>
                )}
                {/* ENG-6311: between the fields and the terms; the buttons only
                    prefill the form, and Join stays its one submit. 32px from
                    the password (its own 8px plus 24px) and from the terms. */}
                {!socialSignup && isSocialSignInEnabled() && (
                  <div className="mb-32 mt-24">
                    {/* Google's button stops at 400px; held to that, Microsoft's matches it. */}
                    <div className="mx-auto w-full max-w-[400px]">
                      <SocialSignInButtons
                        text="signup_with"
                        dividerPlacement="above"
                        dividerLabel="or continue with"
                        // Disabled while connecting, so a late popup answer is dropped.
                        disabled={submitting || socialPending || social?.status === 'CONNECT'}
                        onCredential={(provider, idToken) =>
                          handleSocialCredential(provider, idToken, form)
                        }
                        onError={showSocialFailure}
                      />
                    </div>
                    {social?.status === 'CONFIRM_ACCOUNT' && (
                      <Alert preset="notification" role="status" className="mt-24">
                        <span>{`An OnFrontiers account already uses ${social.email ?? `your ${socialProviderName(social.provider)} email`}. `}</span>
                        <RouterLink to={signinPath} state={social.loginState}>
                          Log in
                        </RouterLink>
                        <span> to continue.</span>
                      </Alert>
                    )}
                  </div>
                )}
                <div className={s.agreements}>
                  <Field
                    type="checkbox"
                    component={Checkbox}
                    classes={termsCheckboxClasses}
                    labelClasses={termsLabelClasses}
                    FormControlProps={{ margin: 'none' }}
                    name="accept_privacy_and_terms_of_use"
                    label={
                      <span>
                        I have read and agree to OnFrontiers{' '}
                        <Link newTab href={PRIVACY_POLICY.url}>
                          {PRIVACY_POLICY.name}
                        </Link>{' '}
                        &amp;{' '}
                        <Link newTab href={TOU_POLICY.url}>
                          {TOU_POLICY.name}
                        </Link>
                      </span>
                    }
                  />
                  {expertAgreements.map((a) => (
                    <Field
                      key={a.policy_code}
                      type="checkbox"
                      component={Checkbox}
                      classes={termsCheckboxClasses}
                      labelClasses={termsLabelClasses}
                      FormControlProps={{ margin: 'none' }}
                      name={`accept_${a.policy_code}`}
                      label={
                        <span>
                          I have read and agree with&nbsp;
                          <Link newTab href={a.policy_url}>
                            {a.policy_label}
                          </Link>
                        </span>
                      }
                    />
                  ))}
                  <Field
                    type="checkbox"
                    component={Checkbox}
                    classes={termsCheckboxClasses}
                    labelClasses={termsLabelClasses}
                    FormControlProps={{ margin: 'none' }}
                    name="accept_sms_opt_in_agreement"
                    label={
                      <span>
                        {`I agree to receive text messages from OnFrontiers, including one-time verification codes and ${isExpertSignup ? 'project' : 'account'} notifications. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.`}
                      </span>
                    }
                  />
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting || socialPending}
                  classes={{ root: s.button }}
                >
                  Join
                </Button>
                <Divider className={s.loginDivider} />
                <div className={s.login}>
                  Already have an account?
                  <Link to={signinPath}> Sign in</Link>
                </div>
              </form>
            );
          }}
        </Form>
      )}
    </AuthLayout>
  );
};

export default connector(Signup);
